/**
 * @file This file provides a utility for mapping file extensions to emoji icons.
 * It includes a default set of icons for common file types, as well as functions for getting, adding, and retrieving icons.
 */

// File type icon mapping utility
// Maps file extensions to their corresponding icons

export const fileTypeIcons = {
  // Document files
  'pdf': '📄',
  'doc': '📝',
  'docx': '📝',
  'txt': '📄',
  'rtf': '📝',
  
  // Spreadsheet files
  'xls': '📊',
  'xlsx': '📊',
  'csv': '📊',
  
  // Presentation files
  'ppt': '📊',
  'pptx': '📊',
  
  // CAD and 3D files
  'dwg': '📐',
  'dxf': '📐',
  'rvt': '🏗️',
  'rfa': '🏗️',
  'ifc': '🏗️',
  'nwd': '🏗️',
  'nwc': '🏗️',
  'nwf': '🏗️',
  'step': '⚙️',
  'stp': '⚙️',
  'iges': '⚙️',
  'igs': '⚙️',
  '3dm': '📦',
  'skp': '📦',
  
  // Image files
  'jpg': '🖼️',
  'jpeg': '🖼️',
  'png': '🖼️',
  'gif': '🖼️',
  'bmp': '🖼️',
  'tiff': '🖼️',
  'tif': '🖼️',
  'svg': '🖼️',
  
  // Video files
  'mp4': '🎥',
  'avi': '🎥',
  'mov': '🎥',
  'wmv': '🎥',
  'flv': '🎥',
  'mkv': '🎥',
  
  // Audio files
  'mp3': '🎵',
  'wav': '🎵',
  'flac': '🎵',
  'aac': '🎵',
  
  // Archive files
  'zip': '📦',
  'rar': '📦',
  '7z': '📦',
  'tar': '📦',
  'gz': '📦',
  
  // Code files
  'js': '💻',
  'html': '🌐',
  'css': '🎨',
  'json': '📋',
  'xml': '📋',
  'sql': '🗃️',
  'py': '🐍',
  'java': '☕',
  'cpp': '⚡',
  'c': '⚡',
  'cs': '🔷',
  
  // Other common files
  'log': '📜',
  'ini': '⚙️',
  'cfg': '⚙️',
  'config': '⚙️',
};

// Default icons
export const defaultIcons = {
  file: '📄',        // Default file icon
  folder: '📁',      // Folder icon (closed)
  folderOpen: '📂',  // Folder icon (open)
  unknown: '❓',     // Unknown file type
  version: '📋',     // Version node icon
  versionIndicator: '🔢', // Indicator for files with multiple versions
};

/**
 * Get the appropriate icon for a file based on its name/extension
 * @param {string} fileName - The name of the file
 * @param {boolean} isFolder - Whether this is a folder
 * @param {boolean} isExpanded - Whether the folder is expanded (only relevant for folders)
 * @param {boolean} hasVersions - Whether this file has multiple versions
 * @param {boolean} isVersion - Whether this is a version node
 * @returns {string} The emoji icon for the file type
 */
export const getFileIcon = (fileName, isFolder = false, isExpanded = false, hasVersions = false, isVersion = false) => {
  // Handle version nodes
  if (isVersion) {
    return defaultIcons.version;
  }
  
  // Handle folders
  if (isFolder) {
    return isExpanded ? defaultIcons.folderOpen : defaultIcons.folder;
  }
  
  // Handle files
  if (!fileName || typeof fileName !== 'string') {
    return defaultIcons.unknown;
  }
  
  // Extract file extension
  const lastDotIndex = fileName.lastIndexOf('.');
  if (lastDotIndex === -1) {
    const baseIcon = defaultIcons.file;
    return hasVersions ? `${defaultIcons.versionIndicator}${baseIcon}` : baseIcon;
  }
  
  const extension = fileName.substring(lastDotIndex + 1).toLowerCase();
  const baseIcon = fileTypeIcons[extension] || defaultIcons.file;
  
  // Add version indicator if file has multiple versions
  return hasVersions ? `${defaultIcons.versionIndicator}${baseIcon}` : baseIcon;
};

/**
 * Add or update a file type icon mapping
 * @param {string} extension - File extension (without dot)
 * @param {string} icon - Emoji icon to use
 */
export const addFileTypeIcon = (extension, icon) => {
  fileTypeIcons[extension.toLowerCase()] = icon;
};

/**
 * Get all available file type mappings
 * @returns {Object} Object containing all file type to icon mappings
 */
export const getAllFileTypeIcons = () => {
  return { ...fileTypeIcons };
};